import axios from 'axios';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import * as Yup from 'yup';
import routes from '../../configs/routes';
import {
  ComplaintAPI,
  ComplaintCategory,
  ComplaintFiledBy,
  ComplaintStatus,
  ComplaintSubCategory,
} from './complaint-api.type';
import {
  getComplaintFiledByDisplay,
  getComplaintStatusDisplay,
  getReasonSubcategoryDisplay,
  toApiFormat,
} from './complaint.utils';

// Mapping of categories to their valid subcategories
const categoryToSubcategories: Record<ComplaintCategory, ComplaintSubCategory[]> = {
  [ComplaintCategory.BUYER]: [
    ComplaintSubCategory.BUYERS_BEHAVIOR,
    ComplaintSubCategory.WRONG_MATERIALS_BUYER,
    ComplaintSubCategory.WRONG_PROPOSITION,
    ComplaintSubCategory.LACK_IN_SPEECH,
    ComplaintSubCategory.OTHER_BUYER,
  ],
  [ComplaintCategory.PACKAGER]: [
    ComplaintSubCategory.WRONG_MATERIALS_PACKAGER,
    ComplaintSubCategory.LACKING_MATERIALS,
    ComplaintSubCategory.OTHER_PACKAGER,
  ],
  [ComplaintCategory.CUSTOMER_SERVICE]: [
    ComplaintSubCategory.CUSTOMER_SERVICE_BEHAVIOR,
    ComplaintSubCategory.OTHER_CUSTOMER_SERVICE,
  ],
  [ComplaintCategory.VALUATION]: [
    ComplaintSubCategory.WRONG_VALUATION,
    ComplaintSubCategory.OTHER_VALUATION,
  ],
  [ComplaintCategory.TELLER]: [
    ComplaintSubCategory.TELLERS_BEHAVIOR,
    ComplaintSubCategory.WRONG_INFOS,
    ComplaintSubCategory.LOW_PRICE,
    ComplaintSubCategory.OTHER_TELLER,
  ],
  [ComplaintCategory.BURSAR]: [
    ComplaintSubCategory.WRONG_NO_RETURN,
    ComplaintSubCategory.OTHER_BURSAR,
  ],
  [ComplaintCategory.LOGISTICS]: [
    ComplaintSubCategory.PACKAGE_MISSING_INCOMING_MH,
    ComplaintSubCategory.PACKAGE_MISSING_OUTGOING_POSTI,
    ComplaintSubCategory.PACKAGE_MISSING_OUTGOING_MH,
    ComplaintSubCategory.OTHER_LOGISTICS,
  ],
  [ComplaintCategory.OTHER]: [ComplaintSubCategory.OTHER_OTHER],
};

const validationSchema = Yup.object().shape({
  dueDate: Yup.date().required('Due date is required'),
  reasonCategory: Yup.string().required('Reason category is required'),
  reasonSubcategory: Yup.string().required('Reason subcategory is required'),
  complaintInfo: Yup.string().required('Complaint information is required'),
  complaintFiledBy: Yup.string().required('Filed by is required'),
  status: Yup.string().required('Status is required'),
  clientNumber: Yup.number().nullable(),
  orderNumber: Yup.number().nullable(),
  complaintDate: Yup.date().nullable(),
});

const ComplaintCreate: React.FC = () => {
  const history = useHistory();
  const initialValues: ComplaintAPI.ComplaintCreateData = {
    dueDate: new Date(),
    reasonCategory: ComplaintCategory.OTHER,
    reasonSubcategory: ComplaintSubCategory.OTHER_OTHER,
    complaintInfo: '',
    complaintFiledBy: ComplaintFiledBy.INTERNAL,
    status: ComplaintStatus.WAITING_CUSTOMER_SERVICE,
    clientNumber: null,
    orderNumber: null,
    complaintDate: new Date(),
  };

  const handleSubmit = async (values: ComplaintAPI.ComplaintCreateData) => {
    try {
      // Transform the complaintFiledBy value to match API expectations
      const transformedValues = {
        ...values,
        complaintFiledBy: toApiFormat(values.complaintFiledBy),
      };

      const response = await axios.post<ComplaintAPI.CreateComplaintResponse>(
        `${window.API_ENDPOINT}/ComplaintHandlers/register`,
        transformedValues
      );
      if (response.data && response.data.complaint) {
        // Redirect to the show page using the complaint ID
        history.push(routes.COMPLAINT.SHOW.replace(':id', response.data.complaint.id));
      }
    } catch (error) {
      console.error('Error creating complaint:', error);
      // TODO: Add error handling UI feedback
    }
  };

  return (
    <Card>
      <CardHeader>
        <h4 className="mb-0">Create New Complaint</h4>
      </CardHeader>
      <CardBody>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="complaintFiledBy">Filed By</Label>
                    <Field as={Input} type="select" name="complaintFiledBy" id="complaintFiledBy">
                      {Object.values(ComplaintFiledBy).map((value) => (
                        <option key={value} value={value}>
                          {getComplaintFiledByDisplay(value)}
                        </option>
                      ))}
                    </Field>
                    {errors.complaintFiledBy && touched.complaintFiledBy && (
                      <div className="text-danger">{errors.complaintFiledBy}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="status">Status</Label>
                    <Field as={Input} type="select" name="status" id="status">
                      {Object.values(ComplaintStatus).map((value) => (
                        <option key={value} value={value}>
                          {getComplaintStatusDisplay(value)}
                        </option>
                      ))}
                    </Field>
                    {errors.status && touched.status && (
                      <div className="text-danger">{errors.status}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="reasonCategory">Reason Category</Label>
                    <Field
                      as={Input}
                      type="select"
                      name="reasonCategory"
                      id="reasonCategory"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newCategory = e.target.value as ComplaintCategory;
                        setFieldValue('reasonCategory', newCategory);
                        // Reset subcategory to first available option for the new category
                        setFieldValue('reasonSubcategory', categoryToSubcategories[newCategory][0]);
                      }}
                    >
                      {Object.values(ComplaintCategory).map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </Field>
                    {errors.reasonCategory && touched.reasonCategory && (
                      <div className="text-danger">{errors.reasonCategory}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="reasonSubcategory">Reason Subcategory</Label>
                    <Field as={Input} type="select" name="reasonSubcategory" id="reasonSubcategory">
                      {categoryToSubcategories[values.reasonCategory].map((value) => (
                        <option key={value} value={value}>
                          {getReasonSubcategoryDisplay(value)}
                        </option>
                      ))}
                    </Field>
                    {errors.reasonSubcategory && touched.reasonSubcategory && (
                      <div className="text-danger">{errors.reasonSubcategory}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="dueDate">Due Date</Label>
                    <Field as={Input} type="date" name="dueDate" id="dueDate" />
                    {errors.dueDate && touched.dueDate && (
                      <div className="text-danger">{errors.dueDate}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="complaintDate">Complaint Date (Optional)</Label>
                    <Field as={Input} type="date" name="complaintDate" id="complaintDate" />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="complaintInfo">Complaint Information</Label>
                <Field
                  as={Input}
                  type="textarea"
                  name="complaintInfo"
                  id="complaintInfo"
                  rows={4}
                />
                {errors.complaintInfo && touched.complaintInfo && (
                  <div className="text-danger">{errors.complaintInfo}</div>
                )}
              </FormGroup>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="clientNumber">Client Number (Optional)</Label>
                    <Field as={Input} type="number" name="clientNumber" id="clientNumber" />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="orderNumber">Order Number (Optional)</Label>
                    <Field as={Input} type="number" name="orderNumber" id="orderNumber" />
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex justify-content-end">
                <Button color="secondary" className="mr-2" href={routes.COMPLAINT.INDEX}>
                  Cancel
                </Button>
                <Button color="primary" type="submit">
                  Create Complaint
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CardBody>
    </Card>
  );
};

export default ComplaintCreate;
